/**
 * Choosing the next animation. Movement only, for now.
 *
 * THE SINGLE MOST IMPORTANT STRUCTURAL FACT: there is no scoring formula. The candidate
 * clips are put through a chain of STABLE sorts, and the LAST sort applied is the primary
 * key; every earlier one survives only as a tie-break, in reverse order of application.
 * Rewriting this as one weighted score compiles, runs, and feels wrong. When everything
 * ties — which happens constantly, because the ratings are coarse — the clip that came
 * first in the library wins, so library order is itself part of the algorithm.
 *
 * The shape for a movement command is:
 *
 *   1. a hard filter over the whole library (`crudeSelection`)
 *   2. two destructive culls that reduce the survivors to ONE outgoing manoeuvre and ONE
 *      finishing look angle
 *   3. the stable-sort cascade, which picks among what is left
 *   4. take the first one
 *
 * Source: humanoid.cpp:1159-1820, animcollection.cpp:493-862, humanoidbase.cpp:875-1229
 * and :1775-2012. Transcribed against the C++.
 */

import { Vector3 } from 'three';
import {
  absoluteAngle2D,
  angle2D,
  clamp,
  fixAngle,
  normalizedOr,
  normalizedOrZero,
  rotate2D,
  vecLength,
} from '../../core/math';
import { numericVariable, variable, vectorVariable, type AnimClip, Foot } from './clip';
import {
  forceIntoAllowedBodyDirectionAngle,
  forceIntoAllowedBodyDirectionVec,
  forceIntoPreferredDirectionAngle,
  forceIntoPreferredDirectionVec,
} from './quantize';
import {
  IDLE_DRIBBLE_SWITCH,
  SPRINT_VELOCITY,
  Velocity,
  WALK_VELOCITY,
  enumToFloatVelocity,
  floatToEnumVelocity,
  rangeVelocity,
  velocityID,
} from './velocity';
import { calculateBiasForFastCornering } from './physics';
import type { SpatialState } from './spatial';

const FORWARD = new Vector3(0, -1, 0);

/**
 * `e_Side` (`gamedefines.hpp:81-83`): left and right, as the original numbers them.
 *
 * Labels now, and only labels — nothing compares them to anything but each other. That was
 * not true while the rotational fence in `crudeSelection` added one of them to an angle: the
 * numbering then decided which clips survived, and differently for each of the two turn
 * directions. Whatever is done here, never put a side into arithmetic; a filter that treats a
 * left turn and its mirror image differently is a filter that makes the game turn one way
 * better than the other. `tests/select.test.ts` pins the symmetry.
 */
const SIDE_LEFT = 0;
const SIDE_RIGHT = 1;

/** What a controller asks a player to do. `gamedefines.hpp:186-210`. */
export interface PlayerCommand {
  desiredFunctionType: string;
  useDesiredMovement: boolean;
  /** Unit vector, world space. */
  desiredDirection: Vector3;
  desiredVelocityFloat: number;
  useDesiredLookAt: boolean;
  /** An absolute position on the pitch, not a direction. */
  desiredLookAt: Vector3;
  /**
   * `e_StrictMovement` (`gamedefines.hpp:205`), read only by an interference command.
   *
   * The struct's own default is Dynamic (`gamedefines.hpp:188`), but the one place that
   * ever builds an interference command always sets this explicitly: True for the ordinary
   * one, False only when the order is to win the ball by any means
   * (`playercontroller.cpp:376-381`). Absent therefore means the ordinary command, which is
   * the strict one. Anything that produces the by-any-means variant — a human holding the
   * close-down button — must set 'false' here, or its player will be culled down to a
   * single quadrant when the original would leave him a wider choice.
   */
  strictMovement?: 'true' | 'false' | 'dynamic';
  /** Ball intent for a pass or shot. Omitted by commands that do not strike the ball. */
  touchInfo?: {
    /** Unit vector, world space. */
    desiredDirection: Vector3;
    /** Normalized input power, normally 0..1. */
    desiredPower: number;
    /**
     * How much easier than usual `shotVector` should treat this particular strike — currently
     * either 0 or `SHOT_BONUS_CONFIDENCE` (`src/sim/controller.ts`). Only ever set by a human's
     * own shot command, for a maxed-charge shot taken with nobody marking him. Absent, or 0,
     * everywhere else, including every shot the computer takes itself, so this changes nothing
     * there.
     */
    confidenceBonus?: number;
  };
  /**
   * Extra metres in front of the foot to plant the ball on a carrying touch.
   *
   * The dribble touch already knocks the ball as far ahead as the speed being asked for
   * deserves, and that is where a sprinting carry gets its length. This is the deliberate
   * shove on top of it — the person's long-touch key — and it is the same number the engine
   * uses to push the ball out of reach of a man breathing down his neck. Zero, and absent,
   * everywhere else.
   */
  frontOfFootOffset?: number;
  /** Keeper deflections may be restricted to clips that retain the ball. */
  pickupBall?: boolean;
  /** Collision-forced fall intent. Only trip commands set these. */
  desiredTripDirection?: Vector3;
  tripType?: number;
  /**
   * Which non-football action this is, when the command asks for one — `specialVar1` and
   * `specialVar2` (`gamedefines.hpp:176-181`, `humanoid.cpp:1351-1352`).
   *
   * The hard filter compares both against every clip and every ordinary clip carries zero in
   * each, so leaving these absent is what has always confined selection to football. Setting
   * `specialVar1` to 1 asks for a happy celebration, 2 for a miserable one and 3 for a referee
   * showing a card; `specialVar2` then picks the register within it — for the celebrations, 1
   * is the one a standing man plays and 2 the one that runs away and decelerates.
   *
   * They are separate from the function type on purpose. A trace records the original's own
   * function numbering, gaps and all, and a type it does not recognise is written as 0 with no
   * error (`src/sim/trace.ts:73-87`), so everything that is not football goes through the one
   * `special` type and is told apart by these two numbers instead.
   */
  specialVar1?: number;
  specialVar2?: number;
}

// ---------------------------------------------------------------------------
// Stage 1 — the hard filter
// ---------------------------------------------------------------------------

export interface CrudeSelectionQuery {
  byFunctionType: boolean;
  functionType: string;

  byIncomingVelocity: boolean;
  incomingVelocity: Velocity;
  incomingVelocityStrict: boolean;
  incomingVelocityForceLinearity: boolean;
  incomingVelocityNoDribbleToIdle: boolean;
  incomingVelocityNoDribbleToSprint: boolean;

  byOutgoingVelocity: boolean;
  outgoingVelocity: Velocity;

  byIncomingBodyDirection: boolean;
  incomingBodyDirection: Vector3;
  incomingBodyDirectionStrict: boolean;
  incomingBodyDirectionForceLinearity: boolean;

  /** Which way the ball is arriving from, in the player's own frame. */
  byIncomingBallDirection: boolean;
  incomingBallDirection: Vector3;
  /** Which way he wants to send it — only ever set for a pass or a shot. */
  byOutgoingBallDirection: boolean;
  outgoingBallDirection: Vector3;
  /** Keeper only: keep the clips that end with the ball in his hands, or the ones that do not. */
  byPickupBall: boolean;
  pickupBall: boolean;
  byTripType: boolean;
  tripType: number;

  bySide: boolean;
  lookAtVecRel: Vector3;

  allowLastDitchAnims: boolean;
  properties: Record<string, string>;
}

/** animcollection.cpp:57 — how far a clip's expected ball approach may be off. */
const MAX_INCOMING_BALL_DIRECTION_DEVIATION = 0.25 * Math.PI;
const MAX_OUTGOING_BALL_DIRECTION_DEVIATION = 0.25 * Math.PI;

export function createQuery(): CrudeSelectionQuery {
  return {
    byFunctionType: false,
    functionType: 'movement',
    byIncomingVelocity: false,
    incomingVelocity: Velocity.Idle,
    incomingVelocityStrict: false,
    incomingVelocityForceLinearity: false,
    incomingVelocityNoDribbleToIdle: false,
    incomingVelocityNoDribbleToSprint: false,
    byOutgoingVelocity: false,
    outgoingVelocity: Velocity.Idle,
    byIncomingBodyDirection: false,
    incomingBodyDirection: FORWARD.clone(),
    incomingBodyDirectionStrict: false,
    incomingBodyDirectionForceLinearity: false,
    byIncomingBallDirection: false,
    incomingBallDirection: new Vector3(),
    byOutgoingBallDirection: false,
    outgoingBallDirection: new Vector3(),
    byPickupBall: false,
    pickupBall: false,
    byTripType: false,
    tripType: 0,
    bySide: false,
    lookAtVecRel: FORWARD.clone(),
    allowLastDitchAnims: false,
    properties: {},
  };
}

const parseFloatOr0 = (s: string): number => {
  const v = parseFloat(s);
  return Number.isNaN(v) ? 0 : v;
};

/**
 * `AnimCollection::CrudeSelection` (animcollection.cpp:493-862) — walk the whole library
 * and keep everything that passes every enabled predicate.
 *
 * Predicates that can only fire for ball-touch commands (incoming and outgoing ball
 * direction, trip type, forced foot, ball pickup) are omitted: their query flags are never
 * set on a movement path, and one of them — forced foot — is commented out at its call
 * site entirely.
 *
 * The result is in library order, which is what makes the later stable sorts deterministic.
 */
export function crudeSelection(library: AnimClip[], query: CrudeSelectionQuery): number[] {
  const result: number[] = [];

  for (let i = 0; i < library.length; i++) {
    const clip = library[i]!;

    // (a) function type
    if (query.byFunctionType && clip.animType !== query.functionType) continue;

    // (b) incoming velocity
    if (query.byIncomingVelocity) {
      const animIn = clip.incomingVelocityEnum;
      if (query.incomingVelocityStrict) {
        if (animIn !== query.incomingVelocity) continue;
      } else {
        // Reachable on a movement path only through the idle fallback below.
        if (
          query.incomingVelocityNoDribbleToIdle &&
          animIn === Velocity.Idle &&
          query.incomingVelocity === Velocity.Dribble
        ) {
          continue;
        }
        if (animIn === Velocity.Idle && query.incomingVelocity === Velocity.Walk) continue;
        if (animIn === Velocity.Idle && query.incomingVelocity === Velocity.Sprint) continue;
        if (animIn !== Velocity.Idle && query.incomingVelocity === Velocity.Idle) continue;
        if (
          query.incomingVelocityNoDribbleToSprint &&
          animIn === Velocity.Sprint &&
          query.incomingVelocity === Velocity.Dribble
        ) {
          continue;
        }
        if (query.incomingVelocityForceLinearity) {
          // The clip's start speed must lie between where the player is now and where the
          // clip ends — no speeding up in order to slow down. Dribbling counts as walking
          // for all three values.
          let ai = rangeVelocity(clip.incomingVelocity);
          let ao = rangeVelocity(clip.outgoingVelocity);
          let qv = enumToFloatVelocity(query.incomingVelocity);
          if (floatToEnumVelocity(ai) === Velocity.Dribble) ai = WALK_VELOCITY;
          if (floatToEnumVelocity(ao) === Velocity.Dribble) ao = WALK_VELOCITY;
          if (floatToEnumVelocity(qv) === Velocity.Dribble) qv = WALK_VELOCITY;
          if (ai > Math.max(qv, ao)) continue;
          if (ai < Math.min(qv, ao)) continue;
        }
      }
    }

    // (c) outgoing velocity — only the idle fallback sets this
    if (query.byOutgoingVelocity && clip.outgoingVelocityEnum !== query.outgoingVelocity) {
      continue;
    }

    if (query.byTripType && parseFloatOr0(variable(clip, 'triptype')) !== query.tripType) continue;

    // (d) rotational side fence — do not turn the LONG way round, past the bearing
    //     opposite to where the player wants to look.
    //
    //     Two tests, and they differ only in where the turn is measured from: the first from
    //     the torso direction the clip was DRAWN starting at, the second from the torso
    //     direction the player actually holds right now. Each asks the same thing — sweeping
    //     from there, in the direction this clip turns, do we cross the forbidden bearing
    //     before we reach the clip's finishing direction? The magnitude test is what tells a
    //     real crossing from a coincidence of signs: if getting there that way would take
    //     more than a half turn, the clip does not go that way round at all, and it is only
    //     the shorter, unfenced route that it actually takes.
    if (query.bySide) {
      const animIncomingDirection = clip.incomingBodyDirection;
      const animOutgoingDirection = rotate2D(clip.outgoingDirection, clip.outgoingBodyAngle);
      const animTurnAngle = angle2D(animOutgoingDirection, animIncomingDirection);
      const fencedDirection = rotate2D(query.lookAtVecRel, Math.PI);
      if (Math.abs(animTurnAngle) > 0.06 * Math.PI) {
        const animSide = animTurnAngle > 0 ? SIDE_LEFT : SIDE_RIGHT;
        const animIncomingToFenceAngle = angle2D(fencedDirection, animIncomingDirection);
        const queryIncomingToFenceAngle = angle2D(fencedDirection, query.incomingBodyDirection);
        const fenceToOutgoingAngle = angle2D(animOutgoingDirection, fencedDirection);
        const animIncomingToFenceSide = animIncomingToFenceAngle > 0 ? SIDE_LEFT : SIDE_RIGHT;
        const queryIncomingToFenceSide = queryIncomingToFenceAngle > 0 ? SIDE_LEFT : SIDE_RIGHT;
        const fenceToAnimOutgoingSide = fenceToOutgoingAngle > 0 ? SIDE_LEFT : SIDE_RIGHT;

        if (
          animIncomingToFenceSide === animSide &&
          fenceToAnimOutgoingSide === animSide &&
          Math.abs(animIncomingToFenceAngle + fenceToOutgoingAngle) < Math.PI
        ) {
          continue;
        }
        // The original wrote `queryIncomingToFenceSide` in the sum here rather than
        // `queryIncomingToFenceAngle` (animcollection.cpp:617) — the side, a 0 or a 1,
        // added to an angle, where the line above it adds the angle. The two equality
        // tests beside it have already tied that angle's sign to the side, so |0 + a| and
        // |1 + a| both came out under a half turn for every angle they could ever hold, and
        // the whole line collapsed into "same side twice, reject" with no distance test at
        // all. That threw away turning clips whose route to the fence is the long way round
        // and which therefore never cross it. The angle is what belongs here, and it is what
        // the line above it uses for exactly the same purpose.
        //
        // MEASURED. Over four unattended matches the correction lets 942,399 more clips past
        // this line, but the filters underneath it — chiefly the incoming body direction —
        // then throw nearly all of them out again, so only 2,288 reach the candidate list,
        // in 494 of 103,421 selections. In none of those did the extra clip go on to be
        // chosen: three whole matches replayed with and without the correction produce the
        // same position for every player on every tick and the same clip for every player on
        // every tick. So it is a correct filter that the game does not currently notice.
        if (
          queryIncomingToFenceSide === animSide &&
          fenceToAnimOutgoingSide === animSide &&
          Math.abs(queryIncomingToFenceAngle + fenceToOutgoingAngle) < Math.PI
        ) {
          continue;
        }
      }
    }

    // (e) does this clip end with the ball in the keeper's hands, or not?
    if (query.byPickupBall) {
      const retains = variable(clip, 'outgoing_retain_state') !== '';
      if (retains !== query.pickupBall) continue;
    }

    // (f) last ditch
    if (!query.allowLastDitchAnims && variable(clip, 'lastditch') === 'true') continue;

    // (g) incoming body direction. Skipped entirely when the query velocity is idle —
    //     which is why a player getting up off the floor may start facing anywhere.
    if (
      query.byIncomingBodyDirection &&
      !(query.byIncomingVelocity && query.incomingVelocity === Velocity.Idle)
    ) {
      const margin = 0.06 * Math.PI;
      if (clip.incomingVelocityEnum !== Velocity.Idle) {
        // Never start from a MORE extreme body angle than the player already holds.
        if (
          Math.abs(fixAngle(absoluteAngle2D(clip.incomingBodyDirection))) >
          Math.abs(fixAngle(absoluteAngle2D(query.incomingBodyDirection))) + margin
        ) {
          continue;
        }
        const difference = Math.abs(
          angle2D(clip.incomingBodyDirection, query.incomingBodyDirection),
        );
        if (query.incomingBodyDirectionStrict) {
          if (difference > margin) continue;
        } else if (difference > 0.5 * Math.PI + margin) {
          continue;
        }

        if (query.incomingBodyDirectionForceLinearity) {
          // The torso must turn ONE way through this clip. If the clip's starting angle
          // sits on the same side of both where the player's torso is now and where the
          // clip finishes, the torso would have to swing out and back — which looks like a
          // stumble. Reject those. The second test catches the case where both angles are
          // on the same side only because they went the long way round.
          const outgoingBodyDir = rotate2D(FORWARD, clip.outgoingBodyAngle + clip.outgoingAngle);
          const toOutgoing = angle2D(clip.incomingBodyDirection, outgoingBodyDir);
          const toQuery = angle2D(clip.incomingBodyDirection, query.incomingBodyDirection);
          if (
            (toOutgoing > margin && toQuery > margin) ||
            (toOutgoing < -margin && toQuery < -margin)
          ) {
            continue;
          }
          if (Math.abs(toOutgoing) + Math.abs(toQuery) > Math.PI + margin) continue;
        }
      } else {
        // Clips that start from a standstill were normalised at load to face forward.
        const difference = Math.abs(angle2D(FORWARD, query.incomingBodyDirection));
        if (query.incomingBodyDirectionStrict) {
          if (difference > margin) continue;
        } else if (difference > 0.25 * Math.PI + margin) {
          continue;
        }
      }
    }

    // (h) which way the ball is arriving from. A clip authored for a ball rolling onto the
    //     player's right foot is no use for one arriving over his left shoulder.
    if (query.byIncomingBallDirection) {
      const animBallDirection = vectorVariable(clip, 'incomingballdirection');
      if (vecLength(animBallDirection) !== 0 && vecLength(query.incomingBallDirection) !== 0) {
        // Height differences count for much less than direction ones.
        animBallDirection.z *= 0.4;
        normalizedOrZero(animBallDirection, animBallDirection);
        const wanted = query.incomingBallDirection.clone();
        wanted.z *= 0.4;
        normalizedOrZero(wanted, wanted);

        const difference = Math.abs(angle2D(wanted, animBallDirection));
        let maxDeviation = Math.abs(numericVariable(clip, 'incomingballdirection_maxdeviation') * Math.PI);
        if (maxDeviation === 0) {
          maxDeviation = MAX_INCOMING_BALL_DIRECTION_DEVIATION;
          if (clip.animType === 'deflect') maxDeviation = 0.4 * Math.PI;
        }
        if (difference > maxDeviation) continue;
      }
    }

    // (i) which way the ball would leave. Only a pass or a shot sets this.
    if (query.byOutgoingBallDirection) {
      const animBallDirection = normalizedOrZero(vectorVariable(clip, 'balldirection'));
      const wanted = normalizedOr(
        _flatOutgoing.set(query.outgoingBallDirection.x, query.outgoingBallDirection.y, 0),
        animBallDirection,
      );
      const difference = Math.abs(angle2D(wanted, animBallDirection));
      let maxDeviation = Math.abs(numericVariable(clip, 'outgoingballdirection_maxdeviation') * Math.PI);
      if (maxDeviation === 0) maxDeviation = MAX_OUTGOING_BALL_DIRECTION_DEVIATION;
      if (difference > maxDeviation) continue;
    }

    // (j) properties equality — always runs
    {
      const q = (key: string): string => query.properties[key] ?? '';
      if (q('incoming_special_state') !== variable(clip, 'incoming_special_state')) continue;
      const queryHasRetain = q('incoming_retain_state') !== '';
      const animHasRetain = variable(clip, 'incoming_retain_state') !== '';
      if (
        (query.functionType === 'deflect' || queryHasRetain !== animHasRetain) &&
        q('incoming_retain_state') !== variable(clip, 'incoming_retain_state')
      ) {
        continue;
      }
      if (parseFloatOr0(q('specialvar1')) !== numericVariable(clip, 'specialvar1')) continue;
      if (parseFloatOr0(q('specialvar2')) !== numericVariable(clip, 'specialvar2')) continue;
    }

    result.push(i);
  }

  return result;
}

// ---------------------------------------------------------------------------
// The comparators
// ---------------------------------------------------------------------------

/**
 * The sort predicates, which the original holds as mutable members on the humanoid and
 * sets just before each sort. Grouped into one object here so the ordering dependency
 * between them is visible: the body-direction rating reads the movement predicate, so the
 * movement one must always be set first.
 */
export interface SortPredicates {
  desiredFoot: Foot;
  incomingVelocity: Velocity;
  relDesiredDirection: Vector3;
  desiredVelocity: Velocity;
  corneringBias: number;
  relIncomingBodyDirection: Vector3;
  lookAt: Vector3;
  numericName: string;
  numericValue: number;
}

export function createPredicates(): SortPredicates {
  return {
    desiredFoot: Foot.Right,
    incomingVelocity: Velocity.Idle,
    relDesiredDirection: FORWARD.clone(),
    desiredVelocity: Velocity.Idle,
    corneringBias: 0,
    relIncomingBodyDirection: FORWARD.clone(),
    lookAt: new Vector3(),
    numericName: '',
    numericValue: 0,
  };
}

/**
 * humanoidbase.cpp:1818-1824 — also computes how much the player should want to slow down
 * before making this turn, which is what makes braking-before-turning emerge with no
 * explicit rule anywhere.
 */
export function setMovementSimilarityPredicate(
  predicates: SortPredicates,
  relDesiredDirection: Vector3,
  desiredVelocity: Velocity,
  spatial: SpatialState,
): void {
  predicates.relDesiredDirection.copy(relDesiredDirection);
  predicates.desiredVelocity = desiredVelocity;
  predicates.corneringBias = calculateBiasForFastCornering(
    // The current movement in ANIM space, where forward is (0,-1,0).
    new Vector3(0, -spatial.floatVelocity, 0),
    relDesiredDirection.clone().multiplyScalar(enumToFloatVelocity(desiredVelocity)),
    1.0,
    0.9,
  );
}

/**
 * "Does this clip go where I want?", in metres per second (humanoidbase.cpp:1826-1869).
 *
 * Two things worth understanding. The cornering bias shrinks the TARGET toward zero when
 * the turn is hard, so decelerating clips start winning — that is the braking behaviour.
 * And the collinearity bonus uses the ABSOLUTE dot product, so a clip that runs exactly
 * AWAY from the wanted direction scores as "straight" as one running toward it; it rewards
 * running in a line, not running the right way.
 */
export function movementSimilarity(clip: AnimClip, p: SortPredicates): number {
  const desiredMovement = p.relDesiredDirection
    .clone()
    .multiplyScalar(enumToFloatVelocity(p.desiredVelocity));
  const outgoingDirection = forceIntoPreferredDirectionVec(clip.outgoingDirection);
  // Note the re-bucketing: a sprint clip reports 7.0, and this lifts it back to 8.0 so it
  // is comparable with a desired velocity built from the gameplay buckets.
  const outgoingVelocity = rangeVelocity(clip.outgoingVelocity);
  const outgoingMovement = outgoingDirection.clone().multiplyScalar(outgoingVelocity);

  desiredMovement.multiplyScalar(1 - p.corneringBias);

  let value = vecLength(desiredMovement.clone().sub(outgoingMovement));
  value -= Math.abs(p.relDesiredDirection.dot(outgoingDirection)) * 4.0;
  return value;
}

/** humanoidbase.cpp:1779-1787 — idle clips count as a match for either foot. */
export function footSimilarity(clip: AnimClip, p: SortPredicates): number {
  if (clip.incomingVelocityEnum === Velocity.Idle) return 0;
  return clip.currentFoot === p.desiredFoot ? 0 : 1;
}

/**
 * humanoidbase.cpp:1793-1816 — how far this clip's starting speed is from the player's,
 * plus a half-point penalty if the clip starts outside the corridor between where he is
 * now and where the clip ends. That penalty is what forbids speeding up in order to slow
 * down.
 */
export function incomingVelocitySimilarity(clip: AnimClip, p: SortPredicates): number {
  const current = velocityID(p.incomingVelocity);
  const animIn = velocityID(clip.incomingVelocityEnum);
  const animOut = velocityID(clip.outgoingVelocityEnum);
  let rating = Math.abs(clamp(animIn - current, -3, 3));
  if (animIn > Math.max(current, animOut)) rating += 0.5;
  if (animIn < Math.min(current, animOut)) rating += 0.5;
  return rating;
}

/** humanoidbase.cpp:1893-1900 — idle clips are always a perfect body-direction match. */
export function incomingBodyDirectionSimilarity(clip: AnimClip, p: SortPredicates): number {
  if (clip.incomingVelocityEnum === Velocity.Idle) return 0;
  return (
    Math.abs(
      angle2D(
        forceIntoAllowedBodyDirectionVec(clip.incomingBodyDirection),
        forceIntoAllowedBodyDirectionVec(p.relIncomingBodyDirection),
      ),
    ) / Math.PI
  );
}

/**
 * "Will I be looking where I want to look when this clip ends?" (humanoidbase.cpp:1906-1966)
 *
 * The look-at target is corrected by the clip's own translation, so a clip that carries the
 * player PAST the target is judged on where the target will be afterwards.
 */
export function bodyDirectionSimilarity(
  clip: AnimClip,
  p: SortPredicates,
  spatial: SpatialState,
): number {
  const translationFactor = 1.0;
  const relDesired = normalizedOr(
    rotate2D(p.lookAt.clone().sub(spatial.position), -spatial.angle).addScaledVector(
      clip.translation,
      -translationFactor,
    ),
    FORWARD,
  );
  const maxAngleSmuggle = 0.1 * Math.PI;
  const outgoingAngle = angle2D(
    rotate2D(
      clip.outgoingDirection,
      clamp(
        angle2D(p.relDesiredDirection, clip.outgoingDirection),
        -maxAngleSmuggle,
        maxAngleSmuggle,
      ),
    ),
    FORWARD,
  );
  const predictedOutgoingBodyDirection = rotate2D(clip.outgoingBodyDirection, outgoingAngle);
  let rating = Math.abs(angle2D(predictedOutgoingBodyDirection, relDesired));
  // A slight standing preference for finishing square to the direction of travel.
  rating += Math.abs(clip.outgoingBodyAngle) * 0.05;
  return rating;
}

/** humanoidbase.cpp:1988-1994 — hand-authored clips before the ones derived from them. */
export function baseAnimSimilarity(clip: AnimClip): number {
  return variable(clip, 'baseanim') === 'true' ? 0 : 1;
}

/** humanoidbase.cpp:1996-2002 — a keeper who can catch prefers catching to parrying. */
export function catchOrDeflectSimilarity(clip: AnimClip): number {
  return variable(clip, 'outgoing_retain_state') !== '' ? 0 : 1;
}

/** humanoidbase.cpp:2004-2012 — a clip that never declares the variable rates as 0. */
export function numericVariableSimilarity(clip: AnimClip, p: SortPredicates): number {
  return Math.abs(numericVariable(clip, p.numericName) - p.numericValue);
}

/**
 * Sort in place by a rating, keeping the order of equal elements.
 *
 * `Array.prototype.sort` has been required to be stable since ES2019, which is what makes
 * this a faithful stand-in for `std::stable_sort`. The stability is not a nicety here: it
 * is how earlier sorts survive as tie-breaks, and it is how library order decides the
 * winner when everything ties.
 */
function stableSortBy(dataSet: number[], rate: (index: number) => number): void {
  const ratings = new Map<number, number>();
  for (const index of dataSet) ratings.set(index, rate(index));
  dataSet.sort((a, b) => ratings.get(a)! - ratings.get(b)!);
}

// ---------------------------------------------------------------------------
// Stage 2 — the two destructive culls
// ---------------------------------------------------------------------------

/**
 * Reduce the set to a single outgoing manoeuvre (humanoidbase.cpp:1103-1171).
 *
 * In the strict form used by movement this keeps only clips sharing the best clip's
 * quadrant — the same quantized combination of finishing speed and turn. Everything that
 * ends up somewhere else is discarded outright, before any of the later sorts get a say.
 */
export function keepBestDirectionAnims(
  library: AnimClip[],
  dataSet: number[],
  predicates: SortPredicates,
  strict: boolean,
  allowedAngle: number,
  allowedVelocitySteps: number,
  quadrantOf: (id: number) => { velocity: Velocity; angle: number },
  preferBaseAnim = false,
): void {
  if (dataSet.length === 0) throw new Error('keepBestDirectionAnims called with an empty set');

  stableSortBy(dataSet, (i) => movementSimilarity(library[i]!, predicates));
  // For a ball touch the clip everything else is measured against should be one of the
  // hand-authored originals, not one of the variations built from it. Skipped for movement,
  // so there the head of the list is purely the best movement match.
  if (strict && preferBaseAnim) stableSortBy(dataSet, (i) => baseAnimSimilarity(library[i]!));
  const bestQuadrantID = library[dataSet[0]!]!.quadrantId;

  for (let i = 1; i < dataSet.length; ) {
    const clip = library[dataSet[i]!]!;
    let keep: boolean;
    if (strict) {
      keep = clip.quadrantId === bestQuadrantID;
    } else {
      const q = quadrantOf(clip.quadrantId);
      const best = quadrantOf(bestQuadrantID);
      keep = true;
      if (variable(clip, 'lastditch') !== 'true') {
        if (
          Math.abs(velocityID(q.velocity, true) - velocityID(best.velocity, true)) >
          allowedVelocitySteps
        ) {
          keep = false;
        }
      }
      if (Math.abs(q.angle - best.angle) > allowedAngle) keep = false;
    }
    if (keep) i++;
    else dataSet.splice(i, 1);
  }
}

/**
 * Reduce the set to a single finishing look angle (humanoidbase.cpp:1174-1229).
 *
 * For movement the tolerance is a hard 10.8 degrees whatever `allowedAngle` says. Note the
 * raw subtraction of two angles with no wrapping — two look angles either side of a full
 * turn read as maximally different rather than as neighbours. That is the original's, and
 * the sets involved stop just short of a half turn, so it rarely bites.
 */
export function keepBestBodyDirectionAnims(
  library: AnimClip[],
  dataSet: number[],
  predicates: SortPredicates,
  spatial: SpatialState,
  strict: boolean,
  allowedAngle: number,
  preferBaseAnim = false,
): void {
  if (dataSet.length === 0) throw new Error('keepBestBodyDirectionAnims called with an empty set');

  stableSortBy(dataSet, (i) => bodyDirectionSimilarity(library[i]!, predicates, spatial));
  if (strict && preferBaseAnim) stableSortBy(dataSet, (i) => baseAnimSimilarity(library[i]!));

  const lookAngleOf = (clip: AnimClip): number =>
    forceIntoAllowedBodyDirectionAngle(clip.outgoingBodyAngle) +
    forceIntoPreferredDirectionAngle(clip.outgoingAngle);

  const bestLookAngle = lookAngleOf(library[dataSet[0]!]!);
  const tolerance = strict ? 0.06 * Math.PI : allowedAngle;

  for (let i = 1; i < dataSet.length; ) {
    if (Math.abs(lookAngleOf(library[dataSet[i]!]!) - bestLookAngle) <= tolerance) i++;
    else dataSet.splice(i, 1);
  }
}

// ---------------------------------------------------------------------------
// The idle fallback
// ---------------------------------------------------------------------------

/**
 * The standing-still clip a player falls back to when nothing else matches
 * (humanoidbase.cpp:875-927).
 *
 * Its own little cascade, with the look-at pointed ten metres straight ahead. Note it
 * asks for clips that both start AND end at a standstill, and forbids last-ditch ones.
 */
export function getIdleMovementAnimID(library: AnimClip[], spatial: SpatialState): number {
  const query = createQuery();
  query.byFunctionType = true;
  query.functionType = 'movement';
  query.byIncomingVelocity = true;
  query.incomingVelocity = Velocity.Idle;
  query.byOutgoingVelocity = true;
  query.outgoingVelocity = Velocity.Idle;

  const dataSet = crudeSelection(library, query);
  if (dataSet.length === 0) {
    throw new Error(
      'No standing-idle movement clip in the library. The animation bundle is incomplete.',
    );
  }

  const predicates = createPredicates();

  predicates.numericName = 'idlelevel';
  predicates.numericValue = 1;
  stableSortBy(dataSet, (i) => numericVariableSimilarity(library[i]!, predicates));

  predicates.relIncomingBodyDirection.copy(FORWARD);
  stableSortBy(dataSet, (i) => incomingBodyDirectionSimilarity(library[i]!, predicates));

  predicates.incomingVelocity = Velocity.Idle;
  stableSortBy(dataSet, (i) => incomingVelocitySimilarity(library[i]!, predicates));

  setMovementSimilarityPredicate(predicates, FORWARD, Velocity.Idle, spatial);
  predicates.lookAt
    .copy(spatial.position)
    .add(rotate2D(new Vector3(0, -10, 0), spatial.angle));
  stableSortBy(dataSet, (i) => bodyDirectionSimilarity(library[i]!, predicates, spatial));

  stableSortBy(dataSet, (i) => movementSimilarity(library[i]!, predicates));

  return dataSet[0]!;
}

// ---------------------------------------------------------------------------
// The whole thing
// ---------------------------------------------------------------------------

export interface SelectionContext {
  library: AnimClip[];
  spatial: SpatialState;
  currentClip: AnimClip;
  /** Where the player wants to look, and whether play has started. */
  inPlay: boolean;
  inSetPiece: boolean;
  /** Distance from the player to where the ball will be in 200 ms. */
  ballDistance: number;
  allowLastDitch: boolean;
  quadrantOf: (id: number) => { velocity: Velocity; angle: number };

  // --- only consulted for ball-touch commands ---
  /** Which way the ball is arriving from, in the player's own frame. */
  ballIncomingDirectionRel?: Vector3;
  /** True when this player has the ball in his hands. */
  isBallRetainer?: boolean;
  /** A high or bouncing ball relaxes the trap culls: reaching it at all is the priority. */
  highOrBouncyBall?: boolean;
  /** `AllowLastDitch(false)` — the version that ignores which side is on top. */
  allowLastDitchIgnoringPossession?: boolean;
}

export interface Selection {
  clipIndex: number;
  /** The body direction the player wants, relative to travel — the solver needs it. */
  desiredBodyDirectionRel: Vector3;
}

export interface Candidates {
  /** Ordered, best first. A touch command walks the whole list; movement takes the head. */
  dataSet: number[];
  desiredBodyDirectionRel: Vector3;
}

/**
 * Build the ordered candidate list for any command — `Humanoid::SelectAnim`'s middle
 * section (humanoid.cpp:1242-1740), stopping short of actually choosing.
 *
 * Returns null when this command should be abandoned so the next one in the player's queue
 * can be tried. That happens for two reasons: no clip of the right type survived the filter,
 * or — for a trap — the best clip available goes somewhere so different from what was asked
 * that going for the ball at all would be a mistake.
 *
 * Does NOT run the physics solver or commit anything. Movement takes the head of the list;
 * a ball touch hands the whole list to the reachability search, which is why this returns a
 * list rather than a winner.
 */
export function selectCandidates(
  context: SelectionContext,
  command: PlayerCommand,
): Candidates | null {
  const { library, spatial, currentClip } = context;
  const commandType = command.desiredFunctionType;
  const isMovement = commandType === 'movement';
  const isPassOrShot =
    commandType === 'shortpass' ||
    commandType === 'longpass' ||
    commandType === 'highpass' ||
    commandType === 'shot';

  // ---- the query -------------------------------------------------------
  const query = createQuery();
  query.byFunctionType = true;
  // A long pass is played with the short-pass clips; only the force differs.
  query.functionType = commandType === 'longpass' ? 'shortpass' : commandType;
  if (commandType === 'trip' && command.tripType !== undefined) {
    query.byTripType = true;
    query.tripType = command.tripType;
  }
  if (commandType === 'deflect' && command.pickupBall === true) {
    query.byPickupBall = true;
    query.pickupBall = true;
  }

  if (command.touchInfo && command.touchInfo.desiredPower !== 0) {
    query.byOutgoingBallDirection = true;
    rotate2D(command.touchInfo.desiredDirection, -spatial.angle, query.outgoingBallDirection);
  }

  query.byIncomingVelocity = true;
  query.incomingVelocity = spatial.enumVelocity;
  // A dribbling run counts as a walk for anything that touches the ball, because the
  // library has no dribble-speed touch clips.
  if (!isMovement && query.incomingVelocity === Velocity.Dribble) {
    query.incomingVelocity = Velocity.Walk;
  }

  if (isMovement || commandType === 'ballcontrol') {
    query.incomingVelocityStrict = true;
  } else if (commandType === 'deflect') {
    query.incomingVelocityForceLinearity = false;
    query.incomingVelocityNoDribbleToSprint = false;
    query.incomingVelocityNoDribbleToIdle = false;
  } else if (isPassOrShot) {
    query.incomingVelocityNoDribbleToSprint = true;
    query.incomingVelocityForceLinearity = false;
    query.incomingVelocityNoDribbleToIdle = false;
  } else {
    query.incomingVelocityNoDribbleToSprint = true;
    query.incomingVelocityForceLinearity = true;
    query.incomingVelocityNoDribbleToIdle = true;
  }

  query.byIncomingBodyDirection = true;
  query.incomingBodyDirection.copy(spatial.relBodyDirectionVec);
  if (isMovement) {
    query.incomingBodyDirectionStrict = true;
  } else if (commandType === 'deflect' || commandType === 'ballcontrol') {
    // Ball control is allowed to twist, so a dribble clip can double as a trap and stop
    // the ball rolling past.
    query.incomingBodyDirectionForceLinearity = false;
  } else {
    query.incomingBodyDirectionForceLinearity = true;
  }

  if (
    command.useDesiredLookAt &&
    variable(currentClip, 'outgoing_special_state') === '' &&
    !context.isBallRetainer
  ) {
    const lookAtVec = normalizedOr(
      command.desiredLookAt.clone().sub(spatial.position),
      spatial.directionVec,
    );
    rotate2D(lookAtVec, -spatial.angle, query.lookAtVecRel);
    query.bySide = true;
  }

  // A trap, a block or a save all depend on which way the ball is coming from.
  if (
    context.ballIncomingDirectionRel &&
    (commandType === 'trap' || commandType === 'interfere' || commandType === 'deflect')
  ) {
    query.byIncomingBallDirection = true;
    query.incomingBallDirection.copy(context.ballIncomingDirectionRel);
  }

  query.allowLastDitchAnims = context.allowLastDitch;
  query.properties['incoming_special_state'] = variable(currentClip, 'outgoing_special_state');
  if (context.isBallRetainer) {
    query.properties['incoming_retain_state'] = variable(currentClip, 'outgoing_retain_state');
  }
  // `humanoid.cpp:1351-1352`, in the original's own order and with its own condition: the
  // property is written only when the command asks for it. Absent, the filter compares the
  // empty string against each clip's number and only the clips carrying zero survive — which
  // is every ordinary clip and none of the eight that are not football.
  if (command.specialVar1 !== undefined) {
    query.properties['specialvar1'] = String(command.specialVar1);
  }
  if (command.specialVar2 !== undefined) {
    query.properties['specialvar2'] = String(command.specialVar2);
  }

  // A player getting up off the floor always starts from a standstill. Note this is set
  // AFTER the strictness decisions, and because the body-direction filter is skipped
  // whenever the query velocity is idle, it quietly disables that filter too.
  if (variable(currentClip, 'outgoing_special_state') !== '') {
    query.incomingVelocity = Velocity.Idle;
  }

  // ---- stage 1 ---------------------------------------------------------
  let dataSet = crudeSelection(library, query);
  if (dataSet.length === 0) {
    // Movement is the only command with a fallback; everything else gives up here and
    // lets the next command in the queue try.
    if (!isMovement) return null;
    dataSet = [getIdleMovementAnimID(library, spatial)];
  }

  const predicates = createPredicates();

  // ---- stage 2 — the destructive culls ---------------------------------
  if (command.useDesiredMovement) {
    const relDesiredDirection = rotate2D(command.desiredDirection, -spatial.angle);
    setMovementSimilarityPredicate(
      predicates,
      relDesiredDirection,
      floatToEnumVelocity(command.desiredVelocityFloat),
      spatial,
    );
    predicates.lookAt.copy(command.desiredLookAt);

    if (isMovement) {
      keepBestDirectionAnims(library, dataSet, predicates, true, 0, 0, context.quadrantOf);
      if (command.useDesiredLookAt) {
        keepBestBodyDirectionAnims(library, dataSet, predicates, spatial, true, 0);
      }
    } else if (commandType === 'ballcontrol') {
      // A player at full stretch drops out of the strict mode (humanoid.cpp:1457-1460).
      // Both allowances passed below are zero, so what that actually changes for him is
      // three things: the manoeuvre cull compares finishing angles instead of quadrant
      // identity, a last-ditch clip is excused the finishing-speed test altogether, and
      // the preference for hand-authored clips inside both culls stops running, so a
      // mirrored or generated clip may head the list.
      const strict = !context.allowLastDitch;
      // Body direction FIRST, then movement — the one command type that does it this way
      // round (humanoid.cpp:1465-1466). Both culls are destructive, so the order decides
      // which clip is left when the best body direction and the best movement match sit in
      // different quadrants, which is the normal case for a dribbler who is turning. The
      // author says as much about the movement path in the variant he left commented out
      // at humanoid.cpp:1443: body first "makes body dirs lots better, at the cost of less
      // correct movement anims".
      if (command.useDesiredLookAt) {
        keepBestBodyDirectionAnims(library, dataSet, predicates, spatial, strict, 0, true);
      }
      keepBestDirectionAnims(library, dataSet, predicates, strict, 0, 0, context.quadrantOf, true);
    } else if (commandType === 'trap') {
      // A trap may end up somewhere other than asked for, and the harder the ball is the
      // more latitude it gets.
      const strict = !(context.allowLastDitchIgnoringPossession || context.highOrBouncyBall);
      const allowedAngle = 0.3 * Math.PI;
      keepBestDirectionAnims(
        library,
        dataSet,
        predicates,
        strict,
        allowedAngle,
        2,
        context.quadrantOf,
        true,
      );
      if (command.useDesiredLookAt) {
        keepBestBodyDirectionAnims(library, dataSet, predicates, spatial, strict, allowedAngle, true);
      }

      // ...but not ANYWHERE. If the best trap available sends him somewhere far from where
      // he wanted to go, he is better off not going for the ball and hoping a later clip
      // saves him. Skipped when the ball is awkward or he is already at full stretch.
      if (!context.highOrBouncyBall && !context.allowLastDitch) {
        const best = library[dataSet[0]!]!;
        const desiredMovement = command.desiredDirection
          .clone()
          .multiplyScalar(command.desiredVelocityFloat);
        const bestMovement = rotate2D(best.outgoingMovement, spatial.angle);
        const currentDesiredDot = command.desiredDirection.dot(spatial.directionVec);

        const angleDiff = Math.abs(
          angle2D(rotate2D(best.outgoingDirection, spatial.angle), command.desiredDirection),
        );
        // 0.375 pi so a straight-on or a 135-degree clip is always acceptable; those two
        // are the ones the library reliably has.
        if (angleDiff > 0.375 * Math.PI) return null;

        const difference = vecLength(bestMovement.sub(desiredMovement));
        if (
          (difference > WALK_VELOCITY + 0.5 && currentDesiredDot > 0) ||
          (difference > SPRINT_VELOCITY + 0.5 && currentDesiredDot <= 0)
        ) {
          return null;
        }
      }
    } else if (commandType === 'interfere') {
      // Only the by-any-means order relaxes this (humanoid.cpp:1544-1547). The ordinary
      // command, which is the one the computer always issues, is strict: the set collapses
      // to a single quadrant instead of keeping everything within 54 degrees of it, and the
      // look-angle tolerance is 10.8 degrees rather than the same 54.
      const strict = (command.strictMovement ?? 'true') === 'true';
      const allowedAngle = 0.3 * Math.PI;
      keepBestDirectionAnims(
        library,
        dataSet,
        predicates,
        strict,
        allowedAngle,
        1,
        context.quadrantOf,
        true,
      );
      if (command.useDesiredLookAt) {
        keepBestBodyDirectionAnims(library, dataSet, predicates, spatial, strict, allowedAngle, true);
      }
    }
  }

  // ---- stage 3: the cascade. Last sort applied wins. --------------------
  predicates.numericName = 'priority';
  predicates.numericValue = 0;
  stableSortBy(dataSet, (i) => numericVariableSimilarity(library[i]!, predicates));

  // BUG, REPRODUCED NOT FIXED (humanoid.cpp:1563-1566): the second test has no `else`, so
  // the value 2 set by the first is overwritten whenever a set piece is running or the
  // ball is far away. The effect is visible — players use less pronounced idling clips
  // while play is stopped than the author evidently intended — so it is part of the feel.
  let desiredIdleLevel = 0;
  if (!context.inPlay) desiredIdleLevel = 2;
  if (context.inSetPiece) desiredIdleLevel = 1;
  else if (context.ballDistance > 16.0) desiredIdleLevel = 1;

  predicates.numericName = 'idlelevel';
  predicates.numericValue = desiredIdleLevel;
  stableSortBy(dataSet, (i) => numericVariableSimilarity(library[i]!, predicates));

  predicates.desiredFoot = spatial.foot;
  stableSortBy(dataSet, (i) => footSimilarity(library[i]!, predicates));

  // Ball control skips this one: which way the torso starts matters less than getting a
  // foot to the ball, and skipping it leaves more clips in contention.
  if (commandType !== 'ballcontrol') {
    predicates.relIncomingBodyDirection.copy(spatial.relBodyDirectionVec);
    stableSortBy(dataSet, (i) => incomingBodyDirectionSimilarity(library[i]!, predicates));
  }

  predicates.incomingVelocity = spatial.enumVelocity;
  stableSortBy(dataSet, (i) => incomingVelocitySimilarity(library[i]!, predicates));

  if (command.desiredTripDirection) {
    const relative = rotate2D(command.desiredTripDirection, -spatial.angle);
    stableSortBy(dataSet, (i) => -vectorVariable(library[i]!, 'bumpdirection').dot(relative));
  }

  // For a ball touch, being a hand-authored clip outranks everything above.
  if (!isMovement) stableSortBy(dataSet, (i) => baseAnimSimilarity(library[i]!));
  if (commandType === 'deflect') {
    stableSortBy(dataSet, (i) => catchOrDeflectSimilarity(library[i]!));
  }

  // So the effective order of importance for movement, most significant first, is:
  //   incoming velocity > incoming body direction > foot > idle level > priority >
  //   library order.
  // Movement similarity and body direction do not appear here at all: they already did
  // their work above, as hard filters rather than as preferences.

  // ---- stage 4 ---------------------------------------------------------
  const desiredBodyDirectionRel = FORWARD.clone();
  if (command.useDesiredLookAt) {
    // Judged from where the player will be a tenth of a second from now, not where he is.
    const ahead = spatial.position.clone().addScaledVector(spatial.movement, 0.1);
    rotate2D(
      normalizedOr(command.desiredLookAt.clone().sub(ahead), FORWARD),
      -spatial.angle,
      desiredBodyDirectionRel,
    );
  }

  return { dataSet, desiredBodyDirectionRel };
}

/** The movement path: build the candidates and take the head of the list. */
export function selectMovementAnim(
  context: SelectionContext,
  command: PlayerCommand,
): Selection {
  const candidates = selectCandidates(context, command);
  if (!candidates) {
    // Unreachable for a movement command, which always falls back to the idle clip.
    throw new Error('movement selection produced no candidates');
  }
  return {
    clipIndex: candidates.dataSet[0]!,
    desiredBodyDirectionRel: candidates.desiredBodyDirectionRel,
  };
}

/**
 * Should a player who already has the ball bother touching it again — `NeedTouch`
 * (humanoid.cpp:1822-1857)?
 *
 * It answers no only for a man standing over the ball: he is barely moving, he has not been
 * asked to move, and the ball is sitting still in front of him, so kicking it again every
 * hundredth of a second would look ridiculous. Anything else — he is turning, he has been
 * asked to go somewhere, the ball is rolling too fast or drifting away from him or heading
 * somewhere he is not — and he plays it.
 *
 * The first test is the one that used to be broken. The original wrote
 * `FloatToEnumVelocity(anim->GetOutgoingVelocity() != e_Velocity_Idle)`, with the comparison
 * INSIDE the classifying call, so what got classified was a 0 or a 1 rather than the clip's
 * finishing speed — and both of those classify as standing still, so the test could never
 * fire. It now asks what it means to ask: a clip that ends the player moving is a clip that
 * has to move the ball too.
 *
 * MEASURED, because the effect is not what the note in the specification predicts. Over
 * eight unattended matches — 16,886 questions across four of them — the corrected test would
 * have changed the answer exactly zero times. Every case it catches is already caught by the
 * test below it, which forces a touch for anyone asked to travel faster than 1.8 m/s, and a
 * player whose clip ends above walking pace has essentially always been asked to do that.
 * So this is the routine saying what it means rather than a change to how the game plays;
 * the long gaps between a dribbler's touches come from somewhere else.
 *
 * With it live, the original's last test — the same "does this clip end moving?" question,
 * asked again to gate a direction check — can no longer be reached, so it is gone. Anyone
 * softening the first test should put it back.
 */
export function needTouch(
  clip: AnimClip,
  command: PlayerCommand,
  spatial: SpatialState,
  ballMovement: Vector3,
  ballSpeed: number,
): boolean {
  if (clip.outgoingVelocityEnum !== Velocity.Idle) return true;
  if (command.desiredVelocityFloat > IDLE_DRIBBLE_SWITCH) return true;
  if (Math.abs(ballSpeed) > 2.0) return true;

  const animMovement = rotate2D(clip.outgoingMovement, spatial.angle)
    .multiplyScalar(0.3)
    .addScaledVector(spatial.movement, 0.7);
  const animVelo = vecLength(animMovement);
  normalizedOr(animMovement, spatial.directionVec, animMovement).multiplyScalar(
    vecLength(spatial.movement) * 0.8 + animVelo * 0.2,
  );

  if (Math.abs(clip.outgoingAngle) > 0.125 * Math.PI) return true;

  if (vecLength(_needTouchScratch.copy(animMovement).sub(ballMovement)) >= 2.0) return true;

  const velocityDeviation = vecLength(animMovement) - vecLength(ballMovement);
  if (velocityDeviation < -1.4 || velocityDeviation >= 0.7) return true;

  return false;
}

const _needTouchScratch = new Vector3();
const _flatOutgoing = new Vector3();
